import React from 'react';

import { useQuery } from '@tanstack/react-query';

import Table from '../../../../../Table';
import FormContainer from '../../../../FormContainer';
import Pagination from '../../../../../Pagination';
import CreateBillButton from './CreateBillButton';

import usePagination from '../../../../../../hooks/usePagination';
import LoanService from '../../../../../../services/LoanService';
import useInsuranceBill from '../../../../../../forms/loan/insuranceBill';
import { useUiContext } from '../../../../../../context/UiContext';

export default function Bills({
  loanId,
  updateInsuranceMutation,
  insurance,
  readOnly,
  refetchInsurance,
}) {
  const { showDialogModal } = useUiContext();

  let hasNoBills = !insurance?.bill_id;

  if (insurance?.bill_id && Array.isArray(insurance.bill_id)) {
    hasNoBills = insurance.bill_id.length === 0;
  }

  const {
    data: bills,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['loanBillsInsurance', loanId],
    queryFn: () =>
      LoanService.getLoanBills({
        loanId,
        unassociatedWithInsurance: false,
        type: ['Insurance'],
        status: null,
      }),
    enabled: !!loanId,
  });

  const refetchEverything = async () => {
    try {
      await refetch();
      await refetchInsurance();
    } catch (error) {
      console.log(error);
    }
  };

  const {
    currentPage,
    switchToPage,
    pageTotal,
    isFetching: areBillsFetching,
    data,
  } = usePagination({
    queryKey: ['loanBillsInsurance', loanId],
    queryFn: LoanService.getLoanBills,
    queryFnArgs: {
      loanId,
      unassociated_with_insurance: false,
      type: ['Insurance'],
      status: null,
    },
    enabled: !!readOnly && !!loanId,
  });

  const addBillToInsurance = async (values) => {
    await updateInsuranceMutation({
      formValues: {
        ...insurance,
        bill_id: [...(insurance?.bill_id || []), values.bill_id],
      },
      noRedirect: true,
    });
    refetch();
  };

  // eslint-disable-next-line no-shadow
  const deleteBill = (billId) => {
    showDialogModal({
      title: 'Delete Bill',
      type: 'danger',
      message: 'Are you sure you want to delete this bill from insurance?',
      onConfirm: async () => {
        await updateInsuranceMutation({
          formValues: {
            ...insurance,
            bill_id: insurance.bill_id.filter((bill) => bill?.id !== billId),
          },
          noRedirect: true,
        });

        refetch();
      },
    });
  };

  const cols = [
    {
      title: 'Status',
      keys: ['status'],
      link: (row) => `/loans/${row.loan_id}/bills/${row?.id}?tab=Bill`,
    },
    {
      title: 'Bill Number',
      keys: ['pixel_bill_number'],
    },
    {
      title: 'Due Date',
      keys: ['due_date'],
      type: 'date',
    },
    {
      title: 'Vendor',
      keys: ['_vendor.name'],
      link: (row) => `/vendors/${row.vendor_id}?tab=Vendor`,
    },
    {
      title: 'Amount',
      keys: ['_amount.total'],
      type: 'currency',
    },
    {
      title: 'Status',
      keys: ['status'],
    },
    {
      title: 'Actions',
      type: 'delete',
      onClick: (row) => deleteBill(row?.id),
    },
  ];

  const currentCols = readOnly
    ? cols.filter((col) => col.type !== 'delete')
    : cols;

  const insuranceBillForm = useInsuranceBill(addBillToInsurance, loanId);
  const insuranceBillIds = insurance?.bill_id?.map((bill) =>
    readOnly ? bill?._bill?.id : bill?.id
  );

  let billsSource = [];

  if (readOnly && data) {
    billsSource = data.items;
  }

  if (!readOnly && bills) {
    billsSource = bills;
  }

  const insuranceBills = billsSource?.filter?.((bill) =>
    insuranceBillIds?.includes(bill?.id)
  );

  return (
    <div className="flex flex-col gap-5">
      <Table
        headerRightContent={
          hasNoBills &&
          !readOnly && (
            <CreateBillButton
              insuranceId={insurance?.id}
              refetch={refetchEverything}
            />
          )
        }
        title="Bills"
        cols={currentCols}
        rows={insuranceBills}
        isFetching={readOnly ? areBillsFetching : isFetching}
        colsWithTotals={['Amount']}
        totalTitle="Total"
      />
      {readOnly && (
        <Pagination
          pageTotal={pageTotal}
          switchToPage={switchToPage}
          currentPage={currentPage}
        />
      )}
      {!readOnly && (
        <FormContainer
          form={insuranceBillForm}
          titleStyle="text-base"
          noBillComId
          inlineSubmitButton
          isSubmitting={insuranceBillForm.formik.isSubmitting}
        />
      )}
    </div>
  );
}
